package com.example.hukakmp.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.example.hukakmp.cameraCaptureAvailable
import com.example.hukakmp.model.EspecieEntry
import com.example.hukakmp.model.FotoSeleccionada
import com.example.hukakmp.model.NuevoParteInput
import com.example.hukakmp.viewmodel.ParteWizardViewModel
import io.github.ismoy.imagepickerkmp.domain.extensions.loadBytes
import io.github.ismoy.imagepickerkmp.features.imagepicker.model.ImagePickerResult
import io.github.ismoy.imagepickerkmp.features.imagepicker.ui.rememberImagePickerKMP

private val tituloPaso = mapOf(
    ParteWizardViewModel.Paso.FECHA_HORA to "Día y horario",
    ParteWizardViewModel.Paso.MODALIDAD to "Modalidad de pesca",
    ParteWizardViewModel.Paso.UBICACION to "Ubicación",
    ParteWizardViewModel.Paso.ESPECIES to "Capturas",
    ParteWizardViewModel.Paso.FOTO to "Fotografía",
    ParteWizardViewModel.Paso.NOTAS to "Observaciones",
)

private val descripcionPaso = mapOf(
    ParteWizardViewModel.Paso.FECHA_HORA to "La fecha y los horarios son obligatorios y no pueden estar en el futuro.",
    ParteWizardViewModel.Paso.MODALIDAD to "Elegí cómo pescaste e indicá las cañas cuando corresponda.",
    ParteWizardViewModel.Paso.UBICACION to "Elegí el punto directamente en el mapa. La ubicación es obligatoria.",
    ParteWizardViewModel.Paso.ESPECIES to "Cargá especies y cantidades. Si no hubo capturas, podés continuar sin agregar ninguna.",
    ParteWizardViewModel.Paso.FOTO to "Adjuntá al menos una foto desde la cámara o la galería.",
    ParteWizardViewModel.Paso.NOTAS to "Las observaciones son opcionales.",
)

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ParteWizardScreen(
    uiState: ParteWizardViewModel.WizardUiState,
    onActualizar: (transform: (NuevoParteInput) -> NuevoParteInput) -> Unit,
    onAgregarEspecie: () -> Unit,
    onActualizarEspecie: (Int, EspecieEntry) -> Unit,
    onQuitarEspecie: (Int) -> Unit,
    onSiguiente: () -> Unit,
    onAnterior: () -> Unit,
    onGuardar: () -> Unit,
    onDescartarBorrador: () -> Unit,
    onCancelar: () -> Unit,
) {
    val pasos = ParteWizardViewModel.Paso.entries
    val indiceActual = pasos.indexOf(uiState.paso)
    val progreso = (indiceActual + 1) / pasos.size.toFloat()
    val imagePicker = rememberImagePickerKMP()
    var pickerError by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(imagePicker.result) {
        when (val result = imagePicker.result) {
            is ImagePickerResult.Success -> {
                val disponibles = ParteWizardViewModel.MAX_FOTOS - uiState.input.fotos.size
                val nuevas = mutableListOf<FotoSeleccionada>()
                var selectionError: String? = null

                result.photos.take(disponibles.coerceAtLeast(0)).forEachIndexed { index, photo ->
                    val bytes = photo.loadBytes()
                    when {
                        bytes.isEmpty() -> selectionError = "No se pudo leer una de las fotografías."
                        bytes.size > ParteWizardViewModel.MAX_FOTO_BYTES ->
                            selectionError = "Una fotografía supera los 10 MB. Elegí una imagen más liviana."
                        else -> nuevas += FotoSeleccionada(
                            nombre = photo.fileName ?: "foto_${uiState.input.fotos.size + index + 1}.jpg",
                            bytes = bytes,
                            contentType = photo.mimeType ?: "image/jpeg",
                        )
                    }
                }

                if (nuevas.isNotEmpty()) {
                    onActualizar { input ->
                        input.copy(fotos = (input.fotos + nuevas).take(ParteWizardViewModel.MAX_FOTOS))
                    }
                }
                pickerError = selectionError
                imagePicker.reset()
            }

            is ImagePickerResult.Error -> {
                pickerError = result.exception.message ?: "No se pudo abrir la cámara o la galería."
                imagePicker.reset()
            }

            is ImagePickerResult.Dismissed -> imagePicker.reset()
            else -> Unit
        }
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
        topBar = {
            TopAppBar(
                title = {
                    Column {
                        Text("Nuevo parte", fontWeight = FontWeight.Bold)
                        Text(
                            "Registro de jornada",
                            style = MaterialTheme.typography.labelSmall,
                            color = Color.White.copy(alpha = 0.75f),
                        )
                    }
                },
                navigationIcon = {
                    TextButton(onClick = onCancelar) {
                        Text("×", color = Color.White, style = MaterialTheme.typography.headlineSmall)
                    }
                },
                actions = {
                    HukaLogoMark(
                        modifier = Modifier.padding(end = 16.dp).size(32.dp),
                        tint = Color.White.copy(alpha = 0.88f),
                    )
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = MaterialTheme.colorScheme.primary,
                    titleContentColor = MaterialTheme.colorScheme.onPrimary,
                ),
            )
        },
        bottomBar = {
            WizardBottomBar(
                canGoBack = indiceActual > 0,
                isLast = uiState.paso == pasos.last(),
                isSaving = uiState.isSaving,
                onAnterior = onAnterior,
                onSiguiente = onSiguiente,
                onGuardar = onGuardar,
            )
        },
    ) { padding ->
        Column(modifier = Modifier.fillMaxSize().padding(padding)) {
            Column(modifier = Modifier.fillMaxWidth().padding(horizontal = 18.dp, vertical = 14.dp)) {
                Text(
                    "PASO ${indiceActual + 1} DE ${pasos.size}",
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.secondary,
                    fontWeight = FontWeight.Bold,
                )
                Text(
                    tituloPaso.getValue(uiState.paso),
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(top = 3.dp),
                )
                Text(
                    descripcionPaso.getValue(uiState.paso),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(top = 4.dp),
                )
                LinearProgressIndicator(
                    progress = { progreso },
                    modifier = Modifier.fillMaxWidth().padding(top = 14.dp),
                    color = MaterialTheme.colorScheme.secondary,
                    trackColor = MaterialTheme.colorScheme.surfaceVariant,
                )

                if (uiState.recuperadoDeBorrador) {
                    Surface(
                        modifier = Modifier.fillMaxWidth().padding(top = 12.dp),
                        shape = RoundedCornerShape(14.dp),
                        color = MaterialTheme.colorScheme.secondaryContainer,
                    ) {
                        Row(
                            modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 9.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Column(modifier = Modifier.weight(1f)) {
                                Text(
                                    "Borrador recuperado",
                                    fontWeight = FontWeight.Bold,
                                    color = MaterialTheme.colorScheme.onSecondaryContainer,
                                )
                                Text(
                                    "Continuamos donde lo dejaste. Las fotos solo se conservan mientras esta pestaña siga abierta.",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSecondaryContainer.copy(alpha = 0.82f),
                                )
                            }
                            TextButton(onClick = onDescartarBorrador) {
                                Text("Descartar")
                            }
                        }
                    }
                }
            }

            Column(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = 16.dp, vertical = 4.dp),
            ) {
                Surface(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(22.dp),
                    color = MaterialTheme.colorScheme.surface,
                    shadowElevation = 1.dp,
                ) {
                    Column(modifier = Modifier.padding(18.dp)) {
                        when (uiState.paso) {
                            ParteWizardViewModel.Paso.FECHA_HORA -> PasoFechaHora(uiState.input, onActualizar)
                            ParteWizardViewModel.Paso.MODALIDAD -> PasoModalidad(uiState.input, onActualizar)
                            ParteWizardViewModel.Paso.UBICACION -> PasoUbicacion(uiState.input, onActualizar)
                            ParteWizardViewModel.Paso.ESPECIES -> PasoEspecies(
                                especies = uiState.input.especies,
                                onAgregar = onAgregarEspecie,
                                onActualizar = onActualizarEspecie,
                                onQuitar = onQuitarEspecie,
                            )

                            ParteWizardViewModel.Paso.FOTO -> PasoFoto(
                                fotos = uiState.input.fotos,
                                cameraAvailable = cameraCaptureAvailable,
                                pickerError = pickerError,
                                pickerLoading = imagePicker.result is ImagePickerResult.Loading,
                                onCamara = {
                                    pickerError = null
                                    imagePicker.launchCamera()
                                },
                                onGaleria = {
                                    pickerError = null
                                    val restantes = ParteWizardViewModel.MAX_FOTOS - uiState.input.fotos.size
                                    imagePicker.launchGallery(
                                        allowMultiple = restantes > 1,
                                        selectionLimit = restantes.coerceAtLeast(1),
                                    )
                                },
                                onQuitar = { index ->
                                    onActualizar { input ->
                                        input.copy(fotos = input.fotos.filterIndexed { i, _ -> i != index })
                                    }
                                },
                            )

                            ParteWizardViewModel.Paso.NOTAS -> PasoNotas(uiState.input, onActualizar)
                        }
                    }
                }

                uiState.error?.let { WizardErrorCard(it) }
                Spacer(Modifier.size(18.dp))
            }
        }
    }
}

@Composable
private fun WizardBottomBar(
    canGoBack: Boolean,
    isLast: Boolean,
    isSaving: Boolean,
    onAnterior: () -> Unit,
    onSiguiente: () -> Unit,
    onGuardar: () -> Unit,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        color = MaterialTheme.colorScheme.surface,
        shadowElevation = 8.dp,
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 12.dp),
            horizontalArrangement = Arrangement.spacedBy(10.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            TextButton(onClick = onAnterior, enabled = canGoBack, modifier = Modifier.weight(0.38f)) {
                Text("Atrás")
            }
            Button(
                onClick = if (isLast) onGuardar else onSiguiente,
                enabled = !isSaving,
                modifier = Modifier.weight(0.62f),
                shape = RoundedCornerShape(14.dp),
                colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.primary),
            ) {
                if (isSaving) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(18.dp),
                        color = MaterialTheme.colorScheme.onPrimary,
                        strokeWidth = 2.dp,
                    )
                    Spacer(Modifier.size(8.dp))
                }
                Text(if (isLast) "Guardar parte" else "Siguiente")
            }
        }
    }
}
